import { usePathname, useRouter } from "next/navigation";
import React from "react";
import { useLocalStorage } from "react-use";
import clientFetcher from "src/lib/fetcher/client";
import { useGlobalContext } from "../../salify-next-layout/default/global-context";

export interface CartStepBarList {
  path: string;
  title: string;
  disabled?: boolean;
}

export interface ICardSectionBazaarContext {
  formValue: Record<string, any>;
  formValid: boolean;
  registerControl(field: any): any;
  updateFormValue(value: any): void;
  stores: any[];
  provinces: any[];
  districts: any[];
  wards: any[];
}
export const CardSectionBazaarContext = React.createContext<ICardSectionBazaarContext>({} as any);
export const useCardSectionContext = () => React.useContext(CardSectionBazaarContext);

export const CardSectionContextProvider = (
  props: React.PropsWithChildren<{
    stepList: CartStepBarList[];
    setStepList: (...args: any[]) => void;
  }>,
) => {
  const router = useRouter();
  const pathname = usePathname();
  const { cartItems: productList } = useGlobalContext();
  const [formValue, updateFormValue] = useLocalStorage<any>(
    "cartForm",
    { paymentMethod: 0, paymentVendor: undefined },
    { raw: false, serializer: (v) => JSON.stringify(v), deserializer: (v) => JSON.parse(v) },
  );
  const [provinces, setProvinces] = React.useState<any[]>([]);
  const [districts, setDistricts] = React.useState<any[]>([]);
  const [wards, setWards] = React.useState<any[]>([]);
  const [stores, setStores] = React.useState<any[]>([]);

  React.useEffect(() => {
    (async () => {
      clientFetcher
        .getLocationProvinces()
        .then((res) =>
          setProvinces(res.map(({ id, code, fullName }) => ({ id, code, label: fullName }))),
        );

      clientFetcher.getLocationDistricts().then((res) =>
        setDistricts(
          res.map(({ id, code, fullName, locationProvinceId: parentId }) => ({
            id,
            code,
            label: fullName,
            parentId,
          })),
        ),
      );

      clientFetcher.getLocationAllWards().then((res) =>
        setWards(
          res.map(({ id, code, fullName, locationDistrictId: parentId }) => ({
            id,
            code,
            label: fullName,
            parentId,
          })),
        ),
      );

      clientFetcher.getStores().then((res) =>
        setStores(
          res.map(({ id, code, address, phone, email }) => ({
            id,
            label: code,
            address,
            phone,
            email,
          })),
        ),
      );
    })();
  }, []);
  const formValid = React.useMemo(() => {
    return !!(
      formValue.shipping_fullName &&
      formValue.shipping_phone &&
      formValue.shipping_store &&
      formValue.shipping_province &&
      formValue.shipping_district &&
      formValue.shipping_ward &&
      formValue.shipping_address
    );
  }, [formValue]);

  React.useEffect(() => {
    const activeIndex = props.stepList.findIndex((step) => step.path === pathname);
    if (activeIndex >= 2 && !productList.length) router.push(props.stepList[0].path);
    if (activeIndex >= 3 && !formValid) router.push(props.stepList[1].path);
  }, [pathname]);

  React.useEffect(() => {
    if (!formValid !== props.stepList[3].disabled) {
      props.setStepList(
        props.stepList.map((item, index) => ({
          ...item,
          disabled: index === 3 ? !(formValid && productList.length) : false,
        })),
      );
    }
  }, [formValid]);

  const value = {
    stores,
    provinces,
    districts,
    wards,
    formValue,
    formValid: formValid,
    updateFormValue: (value) => {
      updateFormValue({ ...formValue, ...value });
    },
    registerControl: (field) => ({
      value: formValue[field] || " ",
      onChange: (event: any) => {
        const newValue = event?.target?.value ?? "";
        updateFormValue({ ...formValue, [field]: newValue });
      },
      onBlur: (event: any) => {
        const newValue = event?.target?.value ?? "";
        updateFormValue({ ...formValue, [field]: newValue.trim() });
      },
    }),
  } as ICardSectionBazaarContext;

  return (
    <CardSectionBazaarContext.Provider value={value}>
      {props.children}
    </CardSectionBazaarContext.Provider>
  );
};
